import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import NeedCard from "@/components/NeedCard";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Sparkles, Plus } from "lucide-react";

const TABS = [
  { key: "all", label: "All" },
  { key: "need", label: "I Need" },
  { key: "have", label: "I Have" },
  { key: "offer", label: "I Offer" },
  { key: "know", label: "I Know" },
];

export default function Needs() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [cats, setCats] = useState(["All"]);
  const intent = params.get("intent") || "all";
  const category = params.get("category") || "All";
  const q = params.get("q") || "";

  useEffect(() => {
    const p = { limit: 48 };
    if (q) p.q = q;
    if (intent !== "all") p.intent = intent;
    if (category !== "All") p.category = category;
    api.get("/needs", { params: p }).then((r) => { setItems(r.data.items); setTotal(r.data.total); setCats(["All", ...(r.data.categories || [])]); });
  }, [q, intent, category]);

  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    if (!v || v === "all" || v === "All") p.delete(k); else p.set(k, v);
    setParams(p);
  };

  return (
    <div>
      <section className="bg-[#F7F4EB] border-b border-[#E2DED0]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
          <div className="inline-flex items-center gap-2 abm-chip">
            <Sparkles className="w-3 h-3" /> Community matching · Washington State
          </div>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl text-[#1E3A2F] leading-tight">
            Post what you need. <span className="text-[#C05A3D]">Find who has it.</span>
          </h1>
          <p className="mt-3 text-[#5C5C5C] max-w-2xl">
            One place for every "does anyone know a…". The whole community shows up when you ask.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link to="/post/need" data-testid="needs-post-btn" className="inline-flex items-center gap-2 rounded-full abm-btn-primary px-5 h-11 font-medium">
              <Plus className="w-4 h-4" /> Post a need or offer
            </Link>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="rounded-2xl bg-white border border-[#E2DED0] p-4 mb-8 flex flex-col md:flex-row md:items-center gap-3">
          <Tabs value={intent} onValueChange={(v) => setParam("intent", v)}>
            <TabsList data-testid="needs-tabs" className="bg-[#F7F4EB] flex-wrap h-auto">
              {TABS.map((t) => (
                <TabsTrigger key={t.key} value={t.key} data-testid={`needs-tab-${t.key}`}>{t.label}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <Select value={category} onValueChange={(v) => setParam("category", v)}>
            <SelectTrigger data-testid="needs-category-select" className="min-w-[200px]"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <div className="flex items-center gap-2 bg-[#F7F4EB] rounded-full px-3 md:ml-auto flex-1">
            <Search className="w-4 h-4 text-[#5C5C5C]" />
            <Input
              data-testid="needs-search-input"
              defaultValue={q}
              onKeyDown={(e) => { if (e.key === "Enter") setParam("q", e.currentTarget.value); }}
              placeholder="Search needs, offers, tags…"
              className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </div>
        </div>

        <div className="text-sm text-[#5C5C5C] mb-4">{total} post{total === 1 ? "" : "s"}</div>

        {items.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-[#E2DED0] rounded-2xl bg-white/60">
            <div className="font-display text-2xl text-[#1E3A2F]">No posts match your filters</div>
            <p className="text-sm text-[#5C5C5C] mt-2">Be the first to ask — the community will answer.</p>
            <Link to="/post/need" className="inline-block mt-5 rounded-full abm-btn-primary px-5 h-10 leading-10 text-white">Post a need</Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((n) => <NeedCard key={n.id} need={n} />)}
          </div>
        )}
      </div>
    </div>
  );
}
